import type {
  Deviation_Survey_Insert_Input,
  Revisions_Scenario_Equipment_Xref_Pumps_Revisions_Insert_Input,
  SizingsManifoldMutationVariables
} from "gql/api";
import { useSizingsManifoldMutation } from "gql/api";
import { haltUntilCompletion } from "./exchanges/mutationLimiter";
import useDestructureScenario from "./useDestructureScenario";
import useCreateRevisionFromQuery from "./useCreateRevisionFromQuery";
import type { ExportedSizing } from "@/utils/exports/exportSizing";
import transplantJournal from "@/utils/transplantJournal";
import { jsonToPostgresArray } from "@/utils/utils-dependent";
import type { Scenario } from "./forms/formTypes";
import { useMutationNotification } from "./logger/mutationNotification";

export default function (
  omitImportedSizingId = false
): (exportedSizing: ExportedSizing) => Promise<ExportedSizing["payload"]> {
  const { executeMutation } = useSizingsManifoldMutation();
  const { registerResultToast } = useMutationNotification();

  return async function (exportedSizing: ExportedSizing) {
    const transplantedSizing = transplantJournal(exportedSizing.payload);

    const sizing = transplantedSizing as ExportedSizing["payload"];

    if (omitImportedSizingId === true) {
      sizing.imported_sizing_id = null;
    }

    const withoutInstalledScenario = useCreateRevisionFromQuery(
      {
        ...sizing,
        installed_scenario_id: null
      },
      ["parent_sizing", "author", "well", "customer", "scenarios"]
    );

    const updateInstalledScenario = useCreateRevisionFromQuery(
      {
        ...sizing,
        winning_revision_id: withoutInstalledScenario.id
      },
      ["parent_sizing", "author", "well", "customer", "scenarios"]
    );

    const scenarios = sizing?.scenarios || [];

    // Ensure scenarios are processed in order of ascending creation date
    // This is the same ordering used during the sizing/scenario query
    const orderedScenarios = [...scenarios].sort(
      (a, b) =>
        new Date(a.journal_created_at).getTime() -
        new Date(b.journal_created_at).getTime()
    );

    const variables = orderedScenarios.reduce<SizingsManifoldMutationVariables>(
      (acc, dereferencedScenario) => {
        const {
          scenario,
          correlations,
          equipment,
          parameters,
          wellProperties,
          deviationSurvey,
          intakes,
          pumps,
          housings,
          cables,
          seals
        } = useDestructureScenario(dereferencedScenario as Scenario);

        const scenario_revision = useCreateRevisionFromQuery(scenario.value, [
          "author",
          "correlations",
          "equipment",
          "parameters",
          "well_properties"
        ]);

        const correlations_revision = useCreateRevisionFromQuery(
          correlations.value
        );

        const deviation_survey_revision = {
          ...useCreateRevisionFromQuery(deviationSurvey.value),
          entry_rows: jsonToPostgresArray(
            deviationSurvey.value?.entry_rows || null
          )
        };

        const equipment_revision = {
          ...useCreateRevisionFromQuery(equipment.value),
          seals_order: jsonToPostgresArray(
            equipment.value?.seals_order || null
          ),
          cables_order: jsonToPostgresArray(
            equipment.value?.cables_order || null
          ),
          pumps_order: jsonToPostgresArray(
            equipment.value?.pumps_order || null
          ),
          intakes_order: jsonToPostgresArray(
            equipment.value?.intakes_order || null
          )
        };

        const parameters_revision = useCreateRevisionFromQuery(
          parameters.value
        );

        const well_properties_revision = useCreateRevisionFromQuery(
          wellProperties.value
        );

        const xref_cables = Object.values(cables.value).map(item =>
          useCreateRevisionFromQuery(item)
        );

        const xref_housings = Object.values(housings.value).map(item =>
          useCreateRevisionFromQuery(item)
        );

        const xref_intakes = Object.values(intakes.value).map(item =>
          useCreateRevisionFromQuery(item)
        );

        const xref_pumps = Object.values(pumps.value)
          .map(item => useCreateRevisionFromQuery(item))
          .map(
            (
              pump: Revisions_Scenario_Equipment_Xref_Pumps_Revisions_Insert_Input
            ) => {
              return {
                ...pump,
                housings_order: jsonToPostgresArray(pump.housings_order || null)
              };
            }
          );

        const xref_seals = Object.values(seals.value).map(item =>
          useCreateRevisionFromQuery(item)
        );

        const xref_uphole_y_tool_bypass_tubings = (
          equipment.value?.r_xref_uphole_y_tool_bypass_tubings || []
        ).map(item => useCreateRevisionFromQuery(item));

        const xref_uphole_y_tool_equipment_tubings = (
          equipment.value?.r_xref_uphole_y_tool_equipment_tubings || []
        ).map(item => useCreateRevisionFromQuery(item));

        const xref_downhole_y_tool_bypass_tubings = (
          equipment.value?.r_xref_downhole_y_tool_bypass_tubings || []
        ).map(item => useCreateRevisionFromQuery(item));

        const xref_downhole_y_tool_equipment_tubings = (
          equipment.value?.r_xref_downhole_y_tool_equipment_tubings || []
        ).map(item => useCreateRevisionFromQuery(item));

        acc.scenarios_revisions.push(scenario_revision);
        acc.scenario_correlations_revisions.push(correlations_revision);
        acc.deviation_survey.push(
          deviation_survey_revision as Deviation_Survey_Insert_Input
        );
        acc.scenario_equipment_revisions.push(
          equipment_revision as Revisions_Scenario_Equipment_Xref_Pumps_Revisions_Insert_Input
        );
        acc.scenario_parameters_revisions.push(parameters_revision);
        acc.scenario_well_properties_revisions.push(well_properties_revision);
        acc.scenario_equipment_xref_cables_revisions.push(...xref_cables);
        acc.scenario_equipment_xref_housings_revisions.push(...xref_housings);
        acc.scenario_equipment_xref_intakes_revisions.push(...xref_intakes);
        acc.scenario_equipment_xref_pumps_revisions.push(
          ...(xref_pumps as Revisions_Scenario_Equipment_Xref_Pumps_Revisions_Insert_Input[])
        );
        acc.scenario_equipment_xref_seals_revisions.push(...xref_seals);
        acc.sc_equip_xref_uphole_ytool_bypass_tubings_revisions.push(
          ...xref_uphole_y_tool_bypass_tubings
        );
        acc.sc_equip_xref_uphole_ytool_equip_tubings_revisions.push(
          ...xref_uphole_y_tool_equipment_tubings
        );
        acc.sc_equip_xref_downhole_ytool_bypass_tubings_revisions.push(
          ...xref_downhole_y_tool_bypass_tubings
        );
        acc.sc_equip_xref_downhole_ytool_equip_tubings_revisions.push(
          ...xref_downhole_y_tool_equipment_tubings
        );

        return acc;
      },
      {
        sizings_revisions: [withoutInstalledScenario],
        scenarios_revisions: [],
        scenario_correlations_revisions: [],
        deviation_survey: [],
        scenario_equipment_revisions: [],
        scenario_parameters_revisions: [],
        scenario_well_properties_revisions: [],
        scenario_equipment_xref_cables_revisions: [],
        scenario_equipment_xref_housings_revisions: [],
        scenario_equipment_xref_intakes_revisions: [],
        scenario_equipment_xref_pumps_revisions: [],
        scenario_equipment_xref_seals_revisions: [],
        sc_equip_xref_uphole_ytool_bypass_tubings_revisions: [],
        sc_equip_xref_uphole_ytool_equip_tubings_revisions: [],
        sc_equip_xref_downhole_ytool_bypass_tubings_revisions: [],
        sc_equip_xref_downhole_ytool_equip_tubings_revisions: []
      }
    );

    const result = executeMutation(variables);
    registerResultToast("Sizing Creation", result);

    await haltUntilCompletion();

    executeMutation({
      sizings_revisions: [updateInstalledScenario],
      scenarios_revisions: [],
      scenario_correlations_revisions: [],
      deviation_survey: [],
      scenario_equipment_revisions: [],
      scenario_parameters_revisions: [],
      scenario_well_properties_revisions: [],
      scenario_equipment_xref_cables_revisions: [],
      scenario_equipment_xref_housings_revisions: [],
      scenario_equipment_xref_intakes_revisions: [],
      scenario_equipment_xref_pumps_revisions: [],
      scenario_equipment_xref_seals_revisions: [],
      sc_equip_xref_uphole_ytool_bypass_tubings_revisions: [],
      sc_equip_xref_uphole_ytool_equip_tubings_revisions: [],
      sc_equip_xref_downhole_ytool_bypass_tubings_revisions: [],
      sc_equip_xref_downhole_ytool_equip_tubings_revisions: []
    });

    await haltUntilCompletion();

    return transplantedSizing as ExportedSizing["payload"];
  };
}
